import { h, Component, Fragment } from 'preact';
import { Text } from 'preact-i18n';
import { v4 as uuidv4 } from 'uuid';

const STATUS = {
    ready: { className: 'bg-success', id: 'raceclasscontestant.import.status-ready', text: 'Ready' },
    alreadyInRace: { className: 'bg-secondary', id: 'raceclasscontestant.import.status-existing', text: 'Already in race' },
    duplicateInFile: { className: 'bg-warning text-dark', id: 'raceclasscontestant.import.status-duplicate', text: 'Duplicate in pasted data' },
    invalid: { className: 'bg-danger', id: 'raceclasscontestant.import.status-invalid', text: 'Invalid data' },
    sourceNotFound: { className: 'bg-danger', id: 'raceclasscontestant.import.status-source-missing', text: 'Source contestant not found' },
    sourceAmbiguous: { className: 'bg-danger', id: 'raceclasscontestant.import.status-source-ambiguous', text: 'Contestant id is not unique' },
    sourceNotAllowed: { className: 'bg-danger', id: 'raceclasscontestant.import.status-source-denied', text: 'No access to source race' },
    sourceMismatch: { className: 'bg-danger', id: 'raceclasscontestant.import.status-source-mismatch', text: 'Source identity does not match' },
    userNotFound: { className: 'bg-danger', id: 'raceclasscontestant.import.status-user-missing', text: 'User not found' },
    ambiguousUser: { className: 'bg-danger', id: 'raceclasscontestant.import.status-user-ambiguous', text: 'Ambiguous user match' },
};

function isSelectable(row) {
    return row.status === 'ready' || row.status === 'duplicateInFile';
}

class RaceClassContestantCsvImport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            step: 1,
            csv: '',
            mappedCsv: '',
            headers: [],
            mappingFields: [],
            columnMapping: {},
            rows: [],
            summary: null,
            selected: {},
            loading: false,
            error: null,
            result: null,
        };
        this.importToken = uuidv4();
        this.importing = false;
    }

    updateCsv = event => {
        const csv = event.target.value;
        this.setState(previous => (csv === previous.csv ? { error: null } : {
            csv,
            mappedCsv: '',
            headers: [],
            mappingFields: [],
            columnMapping: {},
            rows: [],
            summary: null,
            selected: {},
            error: null,
        }));
    }

    goToMapping = async () => {
        const { raceClassId } = this.props;
        const {
            csv, mappedCsv, headers, columnMapping,
        } = this.state;
        if (csv === mappedCsv && headers.length > 0) {
            this.setState({ step: 2, error: null });
            return;
        }
        const { raceClassContestantStore } = this.props.stores;
        this.setState({ loading: true, error: null });
        const response = await raceClassContestantStore.importCsvPreview(raceClassId, csv, {});
        if (response?.status !== 200) {
            this.setState({
                loading: false,
                error: response?.message
                    || <Text id='raceclasscontestant.import.error-headers'>Paste a header row and at least one contestant.</Text>,
            });
            return;
        }
        const mappingFields = response.data.importFields || Object.keys(response.data.detectedMapping || {});
        const explicitMapping = {};
        mappingFields.forEach(field => {
            explicitMapping[field] = response.data.detectedMapping?.[field] || columnMapping[field] || '';
        });
        this.setState({
            step: 2,
            mappedCsv: csv,
            headers: response.data.headers || [],
            mappingFields,
            columnMapping: explicitMapping,
            loading: false,
            error: null,
        });
    }

    setMapping = (field, header) => {
        this.setState(previous => ({
            columnMapping: {
                ...previous.columnMapping,
                [field]: header,
            },
        }));
    }

    preview = async () => {
        const { raceClassId } = this.props;
        const { csv, columnMapping } = this.state;
        const { raceClassContestantStore } = this.props.stores;
        this.setState({ loading: true, error: null });
        const response = await raceClassContestantStore.importCsvPreview(raceClassId, csv, columnMapping);
        if (response?.status === 200) {
            const rows = response.data.rows || [];
            const selected = {};
            rows.forEach(row => {
                selected[row.sourceRow] = row.status === 'ready';
            });
            this.setState({
                step: 3,
                rows,
                summary: response.data.summary,
                selected,
                loading: false,
            });
            return;
        }
        this.setState({
            loading: false,
            error: response?.message || <Text id='raceclasscontestant.import.error-preview'>Could not preview the import.</Text>,
        });
    }

    toggleRow = sourceRow => {
        this.setState(previous => ({
            selected: {
                ...previous.selected,
                [sourceRow]: !previous.selected[sourceRow],
            },
        }));
    }

    toggleAll = () => {
        this.setState(previous => {
            const selectable = previous.rows.filter(isSelectable);
            const allSelected = selectable.length > 0
                && selectable.every(row => previous.selected[row.sourceRow]);
            const selected = { ...previous.selected };
            selectable.forEach(row => {
                selected[row.sourceRow] = !allSelected;
            });
            return { selected };
        });
    }

    importRows = async () => {
        const { raceClassId } = this.props;
        const { csv, columnMapping, rows, selected } = this.state;
        const selectedRows = rows.filter(row => isSelectable(row) && selected[row.sourceRow])
            .map(row => row.sourceRow);
        if (selectedRows.length === 0) {
            this.setState({ error: <Text id='raceclasscontestant.import.error-selection'>Select at least one contestant.</Text> });
            return;
        }
        if (this.importing) {
            return;
        }
        this.importing = true;
        const { raceClassContestantStore } = this.props.stores;
        this.setState({ loading: true, error: null });
        const response = await raceClassContestantStore.importCsvCommit(
            raceClassId,
            csv,
            columnMapping,
            selectedRows,
            this.importToken,
        );
        if (response?.status === 201) {
            this.setState({ result: response.data, loading: false });
            return;
        }
        this.setState({
            loading: false,
            error: response?.message || <Text id='raceclasscontestant.import.error-import'>Could not import contestants.</Text>,
        });
        this.importing = false;
    }

    finish = () => {
        const { callback = () => {} } = this.props;
        callback();
    }

    renderMappingField(field) {
        const { headers, columnMapping } = this.state;
        return (
            <div class='row mb-2 align-items-center' key={field}>
                <label class='col-5 col-form-label text-end' for={`raceContestantImport-${field}`}>
                    <Text id={`raceclasscontestant.import.field-${field}`}>{field}</Text>
                </label>
                <div class='col-7'>
                    <select
                        id={`raceContestantImport-${field}`}
                        class='form-select form-select-sm'
                        value={columnMapping[field] || ''}
                        onChange={event => this.setMapping(field, event.target.value)}
                    >
                        <option value=''>-</option>
                        {headers.map(header => <option value={header} key={header}>{header}</option>)}
                    </select>
                </div>
            </div>
        );
    }

    render() {
        const { raceTitle, raceClassTitle, callback = () => {} } = this.props;
        const {
            step, csv, mappingFields, rows, summary, selected, loading, error, result,
        } = this.state;
        const selectableRows = rows.filter(isSelectable);
        const selectedCount = selectableRows.filter(row => selected[row.sourceRow]).length;
        const allSelected = selectableRows.length > 0
            && selectableRows.every(row => selected[row.sourceRow]);

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 px-4 pt-4' style='max-width: 1100px; padding-bottom: 120px;'>
                    <h5>
                        <i class='fa-duotone fa-file-import me-2' />
                        <Text id='raceclasscontestant.import.title'>Import contestants</Text>
                    </h5>
                    <p class='text-body-secondary mb-3'>
                        {raceTitle}{raceTitle && raceClassTitle ? ' – ' : ''}{raceClassTitle}
                    </p>

                    {error && <div class='alert alert-danger'>{error}</div>}

                    {result && <div class='alert alert-success'>
                        <Text
                            id='raceclasscontestant.import.result'
                            fields={{ inserted: result.inserted, skipped: result.skipped }}
                        >
                            Imported {result.inserted} contestants. Skipped {result.skipped}.
                        </Text>
                    </div>}

                    {!result && step === 1 && <div>
                        <div class='alert alert-info'>
                            <Text id='raceclasscontestant.import.help'>
                                Copy selected rows from last year's contestant CSV, Excel, or Google Sheets,
                                including the header row. Old race state, payments, bibs and results are not imported.
                            </Text>
                        </div>
                        <textarea
                            class='form-control font-monospace'
                            rows='12'
                            value={csv}
                            onInput={this.updateCsv}
                            placeholder={'id\tfirstname\tlastname\temail\tcellphone'}
                        />
                    </div>}

                    {!result && step === 2 && <div>
                        <p class='text-body-secondary'>
                            <Text id='raceclasscontestant.import.mapping-help'>
                                Check every column. An id column is never linked automatically; select it explicitly as the
                                previous contestant id when importing last year's export.
                            </Text>
                        </p>
                        {mappingFields.map(field => this.renderMappingField(field))}
                    </div>}

                    {!result && step === 3 && <div>
                        {summary && <div class='alert alert-light border'>
                            <Text
                                id='raceclasscontestant.import.summary'
                                fields={{ ready: summary.ready || 0, total: summary.total || 0 }}
                            >
                                {summary.ready || 0} of {summary.total || 0} contestants are ready to import.
                            </Text>
                        </div>}
                        <div class='table-responsive' style='max-height: 55vh; overflow-y: auto;'>
                            <table class='table table-sm align-middle'>
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type='checkbox'
                                                class='form-check-input'
                                                checked={allSelected}
                                                onChange={this.toggleAll}
                                                aria-label='Select all importable contestants'
                                            />
                                        </th>
                                        <th><Text id='raceclasscontestant.import.column-row'>Row</Text></th>
                                        <th><Text id='raceclasscontestant.import.column-name'>Name</Text></th>
                                        <th><Text id='raceclasscontestant.import.column-email'>Email</Text></th>
                                        <th><Text id='raceclasscontestant.import.column-phone'>Cellphone</Text></th>
                                        <th><Text id='raceclasscontestant.import.column-status'>Status</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => {
                                        const status = STATUS[row.status] || STATUS.invalid;
                                        return <tr key={row.sourceRow} class={isSelectable(row) ? '' : 'text-body-secondary'}>
                                            <td>
                                                <input
                                                    type='checkbox'
                                                    class='form-check-input'
                                                    checked={!!selected[row.sourceRow]}
                                                    disabled={!isSelectable(row)}
                                                    onChange={() => this.toggleRow(row.sourceRow)}
                                                    aria-label={`Select row ${row.sourceRow}`}
                                                />
                                            </td>
                                            <td>{row.sourceRow}</td>
                                            <td>{row.firstname} {row.lastname}</td>
                                            <td>{row.email}</td>
                                            <td>{row.cellphone}</td>
                                            <td>
                                                <span class={`badge ${status.className}`}>
                                                    <Text id={status.id}>{status.text}</Text>
                                                </span>
                                                {row.sourceDeleted && <small class='d-block mt-1 text-warning'>
                                                    <Text id='raceclasscontestant.import.source-deleted'>Deleted source</Text>
                                                </small>}
                                                {row.validationErrors?.length > 0 && <small class='d-block mt-1'>
                                                    {row.validationErrors.join(' ')}
                                                </small>}
                                            </td>
                                        </tr>;
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>}
                </div>
            </div>

            <div class='fixed-bottom d-flex justify-content-center py-3 px-3 bg-body border-top'>
                {result ? <button type='button' class='btn btn-primary rounded-pill' onClick={this.finish}>
                    <Text id='raceclasscontestant.import.done'>Done</Text>
                </button> : <>
                    <button type='button' class='btn btn-secondary rounded-pill me-2' onClick={step === 1 ? callback : () => this.setState({ step: step - 1, error: null })}>
                        <Text id={step === 1 ? 'raceclasscontestant.import.cancel' : 'raceclasscontestant.import.back'}>
                            {step === 1 ? 'Cancel' : 'Back'}
                        </Text>
                    </button>
                    {step === 1 && <button
                        type='button'
                        class='btn btn-primary rounded-pill'
                        disabled={loading || !csv.trim()}
                        onClick={this.goToMapping}
                    >
                        <Text id='raceclasscontestant.import.next'>Next</Text>
                    </button>}
                    {step === 2 && <button
                        type='button'
                        class='btn btn-primary rounded-pill'
                        disabled={loading}
                        onClick={this.preview}
                    >
                        {loading ? <span class='spinner-border spinner-border-sm me-2' /> : null}
                        <Text id='raceclasscontestant.import.preview'>Preview</Text>
                    </button>}
                    {step === 3 && <button
                        type='button'
                        class='btn btn-primary rounded-pill'
                        disabled={loading || selectedCount === 0}
                        onClick={this.importRows}
                    >
                        {loading ? <span class='spinner-border spinner-border-sm me-2' /> : null}
                        <Text id='raceclasscontestant.import.import-selected' fields={{ count: selectedCount }}>
                            Import {selectedCount} contestants
                        </Text>
                    </button>}
                </>}
            </div>
        </>);
    }
}

export {
    isSelectable,
};
export default RaceClassContestantCsvImport;
